import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

export const UpdatePrompt: React.FC = () => {
  const [hasPrompted, setHasPrompted] = useState(false);

  useEffect(() => {
    // If we're in development, skip this check
    if (import.meta.env.DEV) return;

    // Check for updates every 5 minutes
    const CHECK_INTERVAL = 5 * 60 * 1000;

    const checkForUpdate = async () => {
      if (hasPrompted) return; // Don't annoy the user if they already saw the prompt

      try {
        // Fetch the version.json file, appending a cache-busting timestamp
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;

        const data = await res.json();
        
        // Compare the server's version against the version built into our currently running app
        if (data.version && data.version !== __APP_VERSION__) {
          setHasPrompted(true);
          
          toast(
            (t) => (
              <div className="flex flex-col gap-2">
                <span className="font-semibold text-white">
                  Update Available! 🎉
                </span>
                <span className="text-sm text-white">
                  A new version of Idearify is available. Refresh to get the latest features!
                </span>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      if ('caches' in window) {
                        caches.keys().then((names) => {
                          for (const name of names) caches.delete(name);
                        });
                      }
                      // Use a query parameter to force the browser/CDN to fetch a fresh index.html
                      const newUrl = new URL(window.location.href);
                      newUrl.searchParams.set('v', data.version);
                      window.location.href = newUrl.toString();
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Refresh Now
                  </button>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Later
                  </button>
                </div>
              </div>
            ),
            {
              duration: Infinity, // Keep the toast open until they interact
              position: 'bottom-right',
              // Pinned, not `dark:`-based: Tailwind's dark variant follows the
              // OS preference, which left dark text on the dark toast.
              style: {
                minWidth: '300px',
                background: '#363636',
                color: '#fff',
              }
            }
          );
        }
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    };

    // Initial check
    checkForUpdate();

    // Set up polling
    const intervalId = setInterval(checkForUpdate, CHECK_INTERVAL);

    // Also check when the user focuses the window
    const handleFocus = () => checkForUpdate();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [hasPrompted]);

  return null; // This component doesn't render anything directly
};
